import {
  AmplifyProvider,
  Authenticator,
  createTheme,
} from "@aws-amplify/ui-react";
import { lazy, Suspense } from "react";
import Loader from "./components/Loader";
import { theme } from "./theme";

// Branding for both the Authenticator (login screen) and every Amplify
// component in the app — adapted from admin-frontend's `App.tsx` so this
// tool reads as part of the same platform.
const amplifyTheme = createTheme({
  name: "er-system-theme",
  tokens: {
    shadows: {
      small: { value: "none" },
      medium: { value: "none" },
      large: { value: "none" },
    },
    colors: {
      brand: {
        primary: {
          10: { value: "hsl(220, 50%, 96%)" },
          20: { value: "hsl(220, 50%, 88%)" },
          40: { value: "hsl(220, 49%, 72%)" },
          60: { value: "#547ac1" },
          80: { value: "#547ac1" },
          90: { value: "#3d6bb0" },
          100: { value: "#2f5a9e" },
        },
      },
      border: {
        primary: { value: "#e6ebf1" },
        secondary: { value: "#eef2f7" },
      },
      background: {
        secondary: { value: "#f6f9fc" },
      },
      font: {
        secondary: { value: "#425466" },
        tertiary: { value: "#697386" },
      },
    },
    components: {
      button: {
        borderRadius: { value: "8px" },
        primary: {
          _loading: {
            backgroundColor: { value: "#547ac1" },
            color: { value: "rgba(255,255,255,0.7)" },
            borderColor: { value: "transparent" },
          },
          _disabled: {
            backgroundColor: { value: "#547ac1" },
            color: { value: "rgba(255,255,255,0.55)" },
            borderColor: { value: "transparent" },
          },
        },
      },
      fieldcontrol: {
        borderColor: { value: theme.lines },
        borderWidth: { value: "1px" },
        borderRadius: { value: "8px" },
        _focus: {
          borderColor: { value: theme.primaryColor },
          boxShadow: { value: "none" },
        },
      },
    },
  },
});

// Code-split the router (and everything it pulls in) from the auth
// boilerplate, same split admin-frontend uses.
const Router = lazy(() => import("./Router"));

/**
 * Two-factor is REQUIRED on this pool (`MfaConfiguration: ON`, TOTP only — see
 * nest-backend/scripts/ops-pool-mfa.ts). The Authenticator handles both the
 * one-time QR enrolment and the per-login code prompt on its own; the only thing
 * worth configuring is what an authenticator app calls the entry, since the
 * Cognito default ("AWSCognito") is meaningless in a list of codes and is baked
 * into the QR at enrolment time.
 */
const authenticatorFormFields = {
  setupTOTP: {
    QR: { totpIssuer: "Enhanced Rentals Ops" },
    confirmation_code: {
      label: "Code from your authenticator app",
      placeholder: "6-digit code",
    },
  },
  confirmSignIn: {
    confirmation_code: {
      label: "Code from your authenticator app",
      placeholder: "6-digit code",
    },
  },
};

export default () => (
  <AmplifyProvider theme={amplifyTheme}>
    {/* Sign-up is disabled — accounts in the ops pool are admin-created,
        not self-service (see scripts/create-ops-user.ts in nest-backend). */}
    <Authenticator hideSignUp formFields={authenticatorFormFields}>
      <Suspense fallback={<Loader label="Loading…" />}>
        <Router />
      </Suspense>
    </Authenticator>
  </AmplifyProvider>
);
